import { useState, useEffect, useRef, useCallback } from "react";
import { X, Plus, Loader, Check, Search, ChevronDown, Upload, FileText, AlertCircle } from "lucide-react";
import axiosInstance from "../../API/interceptor";
import RichTextEditor from "@dietary-business-intelligence/textora";
import { stripHtml } from "../../utils/sanitizeInput";

/* ── helpers ── */
// Cross-browser date+time picker (Firefox fix for datetime-local)
function DateTimePicker({ value, onChange, min, max, required }) {
  const date    = value ? value.split('T')[0] : '';
  const time    = value ? (value.split('T')[1] || '') : '';
  const minDate = min ? min.split('T')[0] : undefined;
  const maxDate = max ? max.split('T')[0] : undefined;
  const handle  = (type, val) => {
    if (type === 'date') onChange(val ? `${val}T${time || '00:00'}` : '');
    else onChange(date ? `${date}T${val}` : '');
  };

  return (
    <div className="flex gap-1.5">
      <input type="date" value={date} min={minDate} max={maxDate} required={required}
        onChange={e => handle('date', e.target.value)}
        className="flex-[1_1_55%] px-2.5 py-[7px] rounded-lg border border-slate-200 text-[13px] outline-none font-[inherit] box-border text-gray-700 bg-white" />
      <input type="time" value={time}
        onChange={e => handle('time', e.target.value)}
        className="flex-[1_1_45%] px-2.5 py-[7px] rounded-lg border border-slate-200 text-[13px] outline-none font-[inherit] box-border text-gray-700 bg-white" />
    </div>
  );
}

function resizeImagesTo25(html) {
  if (!html || !html.includes('<img')) return html;
  try {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('img').forEach(img => {
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.style.width    = '25%';
      img.style.height   = 'auto';
      img.style.maxWidth = '25%';
    });
    return div.innerHTML;
  } catch { return html; }
}

function EditorWrapper({ editorNode }) {
  const divRef = useRef(null);
  const obsRef = useRef(null);
  const [previewSrc, setPreviewSrc] = useState(null);

  useEffect(() => {
    const el = divRef.current;
    if (!el) return;

    const applySize = img => {
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.style.width    = '25%';
      img.style.height   = 'auto';
      img.style.maxWidth = '25%';
      img.style.cursor   = 'zoom-in';
    };

    const onDblClick = e => {
      if (e.target.tagName === 'IMG') setPreviewSrc(e.target.src);
    };

    el.addEventListener('dblclick', onDblClick);

    obsRef.current = new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') applySize(node);
          node.querySelectorAll?.('img').forEach(applySize);
        });
        if (m.type === 'attributes' && m.target.tagName === 'IMG') applySize(m.target);
      });
    });
    obsRef.current.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['width', 'height', 'style'] });

    return () => {
      obsRef.current?.disconnect();
      el.removeEventListener('dblclick', onDblClick);
    };
  }, []);

  return (
    <>
      <div ref={divRef} className="[resize:vertical] overflow-hidden min-h-[180px] border border-slate-200 rounded-lg">
        {editorNode}
      </div>
      {previewSrc && (
        <div onClick={() => setPreviewSrc(null)}
          className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur flex items-center justify-center cursor-zoom-out">
          <button onClick={() => setPreviewSrc(null)}
            className="absolute top-4 right-5 w-9 h-9 rounded-full bg-white/20 border-none flex items-center justify-center cursor-pointer">
            <X size={18} color="#fff" />
          </button>
          <img src={previewSrc} alt="preview" onClick={e => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] rounded-xl shadow-[0_25px_60px_rgba(0,0,0,.5)] cursor-default" />
        </div>
      )}
    </>
  );
}

const colorMap  = ["#6366F1", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#3B82F6"];
const initColor = (n = "") => colorMap[(n || "").charCodeAt(0) % colorMap.length];

function Av({ name = "?", size = 24 }) {
  const ini = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35, background: initColor(name) }}>
      {ini}
    </span>
  );
}

/* ── workspace cache ── */
let _wsCache = null;
async function resolveWorkspaceName(productCode) {
  if (!productCode) return null;
  if (!_wsCache) {
    try {
      const res = await axiosInstance.get("/task_assignment/get_product_codes");
      const arr = Array.isArray(res.data?.product_codes) ? res.data.product_codes
        : Array.isArray(res.data) ? res.data : [];
      _wsCache = {};
      arr.forEach(item => {
        if (typeof item === "object") {
          const key = Object.keys(item)[0];
          _wsCache[String(key)] = item[key];
        }
      });
    } catch { _wsCache = {}; }
  }
  return _wsCache[String(productCode)] || null;
}

/* ── date helpers ── */
const toInputVal = (v) => {
  if (!v) return "";
  const dt = new Date(v);
  if (isNaN(dt)) return "";
  return dt.toISOString().slice(0, 16);
};

/* ── User search dropdown ── */
function UserSelect({ label, value, onChange, multiple = false, required = false, error }) {
  const [open,    setOpen]    = useState(false);
  const [search,  setSearch]  = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debRef   = useRef(null);
  const abortRef = useRef(null);
  const wrapRef  = useRef(null);

  const doSearch = useCallback((term) => {
    clearTimeout(debRef.current);
    abortRef.current?.abort();
    debRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const res = await axiosInstance.get("/task_assignment/get_users_all", {
          params: { q: term || "" },
          signal: abortRef.current.signal,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setResults(data.map(u => ({
          value: String(u.staffid),
          label: u.display_name || u.alias_name || String(u.staffid),
        })));
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setResults([]);
      } finally { setLoading(false); }
    }, 300);
  }, []);

  useEffect(() => { if (open) doSearch(""); }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isSelected = (val) => multiple
    ? (Array.isArray(value) ? value.some(v => v.value === val) : false)
    : value?.value === val;

  const toggle = (val, lbl) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (arr.some(v => v.value === val)) onChange(arr.filter(v => v.value !== val));
      else onChange([...arr, { value: val, label: lbl }]);
    } else {
      onChange({ value: val, label: lbl });
      setOpen(false);
    }
  };

  const displayLabel = () => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.length) return <span className="text-slate-400 text-[13px]">Select...</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {arr.map(v => (
            <span key={v.value} className="bg-[#EEEDFE] text-[#3C3489] text-[11px] font-semibold py-0.5 px-2 rounded-full border-[0.5px] border-[#AFA9EC] flex items-center gap-1">
              {v.label}
              <button onClick={e => { e.stopPropagation(); onChange(arr.filter(x => x.value !== v.value)); }}
                className="bg-transparent border-none cursor-pointer text-[#3C3489] text-[13px] leading-none p-0">×</button>
            </span>
          ))}
        </div>
      );
    }
    if (value?.label) return <span className="text-gray-700 text-[13px]">{value.label}</span>;
    return <span className="text-slate-400 text-[13px]">Select...</span>;
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="text-[11px] font-semibold text-slate-500 block mb-[3px]">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <div onClick={() => setOpen(p => !p)}
        className={`min-h-[36px] px-2.5 py-1.5 rounded-lg border cursor-pointer bg-white flex items-center justify-between gap-1.5 box-border ${open ? 'border-[rgb(124,132,207)]' : error ? 'border-red-300' : 'border-slate-200'}`}>
        <div className="flex-1 min-w-0">{displayLabel()}</div>
        <ChevronDown size={12} color="#94A3B8"
          className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </div>
      {error && <div className="text-[11px] text-red-500 mt-1">{error}</div>}
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,.12)] z-[300] overflow-hidden border-[0.5px] border-[#E8EAF0]">
          <div className="py-[7px] px-2.5 border-b-[0.5px] border-b-[#F1F5F9]">
            <div className="flex items-center gap-1.5 py-1 px-2 rounded-[7px] border-[0.5px] border-[#E8EAF0] bg-slate-50">
              <Search size={12} color="#94A3B8" />
              <input autoFocus value={search}
                onChange={e => { setSearch(e.target.value); doSearch(e.target.value); }}
                placeholder="Search..."
                className="border-none outline-none bg-transparent text-[12px] text-gray-700 w-full font-[inherit]" />
            </div>
          </div>
          <div className="max-h-[170px] overflow-y-auto">
            {loading ? (
              <div className="p-3.5 text-center"><Loader size={15} color="rgb(124,132,207)" /></div>
            ) : results.length === 0 ? (
              <div className="p-3 text-center text-[12px] text-slate-400">No users found</div>
            ) : results.map(u => {
              const sel = isSelected(u.value);
              return (
                <div key={u.value} onClick={() => toggle(u.value, u.label)}
                  className={`flex items-center gap-[9px] px-3 py-[7px] cursor-pointer border-b-[0.5px] border-b-[#F9FAFB] ${sel ? 'bg-[#EEEDFE]' : 'bg-white hover:bg-slate-50'}`}>
                  {multiple && (
                    <div className={`w-3.5 h-3.5 rounded shrink-0 border-2 flex items-center justify-center ${sel ? 'border-[rgb(124,132,207)] bg-[rgb(124,132,207)]' : 'border-gray-300 bg-white'}`}>
                      {sel && <Check size={8} color="#fff" />}
                    </div>
                  )}
                  <Av name={u.label} size={20} />
                  <span className="text-[12px] text-gray-700">{u.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Category multi-select dropdown with search ── */
function CatSelect({ categories, value, onChange }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered   = categories.filter(c => c.toLowerCase().includes(search.toLowerCase()));
  const isSelected = (cat) => value.includes(cat);
  const toggle     = (cat) => {
    if (isSelected(cat)) onChange(value.filter(c => c !== cat));
    else onChange([...value, cat]);
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="text-[11px] font-semibold text-slate-500 block mb-[3px]">
        Functional Categories
      </label>
      <div onClick={() => setOpen(p => !p)}
        className={`min-h-[36px] px-2.5 py-1.5 rounded-lg border cursor-pointer bg-white flex items-center justify-between gap-1.5 box-border ${open ? 'border-[rgb(124,132,207)]' : 'border-slate-200'}`}>
        <div className="flex-1 min-w-0">
          {value.length === 0 ? (
            <span className="text-slate-400 text-[13px]">Select categories...</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {value.map((cat, i) => (
                <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[11px] font-semibold py-0.5 px-2 rounded-full border-[0.5px] border-[#AFA9EC] flex items-center gap-1">
                  {cat}
                  <button onClick={e => { e.stopPropagation(); toggle(cat); }}
                    className="bg-transparent border-none cursor-pointer text-[#3C3489] text-[13px] leading-none p-0">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronDown size={12} color="#94A3B8"
          className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,.12)] z-[300] overflow-hidden border-[0.5px] border-[#E8EAF0]">
          <div className="py-[7px] px-2.5 border-b-[0.5px] border-b-[#F1F5F9]">
            <div className="flex items-center gap-1.5 py-1 px-2 rounded-[7px] border-[0.5px] border-[#E8EAF0] bg-slate-50">
              <Search size={12} color="#94A3B8" />
              <input autoFocus value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search categories..."
                className="border-none outline-none bg-transparent text-[12px] text-gray-700 w-full font-[inherit]" />
            </div>
          </div>
          <div className="max-h-[170px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-center text-[12px] text-slate-400">No categories found</div>
            ) : filtered.map((cat, i) => {
              const sel = isSelected(cat);
              return (
                <div key={i} onClick={() => toggle(cat)}
                  className={`flex items-center gap-[9px] px-3 py-[7px] cursor-pointer border-b-[0.5px] border-b-[#F9FAFB] ${sel ? 'bg-[#EEEDFE]' : 'bg-white hover:bg-slate-50'}`}>
                  <div className={`w-3.5 h-3.5 rounded shrink-0 border-2 flex items-center justify-center ${sel ? 'border-[rgb(124,132,207)] bg-[rgb(124,132,207)]' : 'border-gray-300 bg-white'}`}>
                    {sel && <Check size={8} color="#fff" />}
                  </div>
                  <span className="text-[12px] text-gray-700">{cat}</span>
                </div>
              );
            })}
          </div>
          {value.length > 0 && (
            <div className="px-3 py-1.5 border-t-[0.5px] border-t-[#F1F5F9] bg-slate-50 flex justify-between items-center">
              <span className="text-[11px] text-slate-500">{value.length} selected</span>
              <button onClick={() => onChange([])}
                className="text-[11px] text-red-500 bg-transparent border-none cursor-pointer font-[inherit]">Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── shared input class ── */
const inputCls = "w-full px-2.5 py-[7px] rounded-lg border border-slate-200 text-[13px] outline-none font-[inherit] box-border text-gray-700 bg-white";

const Label = ({ txt, required }) => (
  <label className="text-[11px] font-semibold text-slate-500 block mb-[3px]">
    {txt}{required && <span className="text-red-500">*</span>}
  </label>
);

/* ══ MAIN ══ */
export default function CreateSubtaskModal({ task, onClose, onSuccess, showToast }) {
  const [form, setForm] = useState({
    task_type:             "",
    detailedDescription:   "",
    priority:              "MEDIUM",
    start_date:            "",
    completion_sla:        "",
    assigned_to:           null,
    created_by:            null,
    observer:              [],
    informer:              [],
    functional_categories: [],
    subtaskComment:        "",
  });

  const [errors,        setErrors]        = useState({});
  const [descKey,       setDescKey]       = useState(0);
  const [attachment,    setAttachment]    = useState(null);
  const [submitting,    setSubmitting]    = useState(false);
  const [categories,    setCategories]    = useState([]);
  const [priorityList,  setPriorityList]  = useState([]);
  const [workspaceName, setWorkspaceName] = useState(task.product_name || null);
  const [dragging,      setDragging]      = useState(false);
  const fileRef = useRef();

  const set = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    setErrors(p => {
      if (!p[key]) return p;
      const n = { ...p };
      delete n[key];
      return n;
    });
  };

  // ✅ Parent task deadline as max for date inputs
  const parentDeadlineMax   = toInputVal(task.completion_sla);
  // ✅ Parent task created_at as min for start date
  const parentCreatedAtMin  = toInputVal(task.task_created_at);
  const parentDeadlineLabel = parentDeadlineMax
    ? new Date(task.completion_sla).toLocaleDateString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric"
      })
    : null;

  useEffect(() => {
    // Load priority list from DB
    axiosInstance.post("/task_assignment/task_priority")
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setPriorityList(data.map(p => p.priority).filter(Boolean));
      })
      .catch(() => {});

    // Load functional categories
    axiosInstance.get("/task_assignment/get_functional_categories")
      .then(res => {
        if (Array.isArray(res.data)) setCategories(res.data);
      })
      .catch(() => {});

    // Resolve workspace name if missing
    if (!task.product_name && task.product_code) {
      resolveWorkspaceName(task.product_code).then(name => {
        if (name) setWorkspaceName(name);
      });
    }
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.task_type.trim())   errs.task_type      = "Subtask name is required";
    if (!form.completion_sla)     errs.completion_sla = "Due date is required";
    if (!form.assigned_to?.value) errs.assigned_to    = "Responsible is required";
    if (!form.created_by?.value)  errs.created_by     = "Accountable is required";
    setErrors(errs);
    if (Object.keys(errs).length) {
      showToast(Object.values(errs)[0], "error");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("task_type",           stripHtml(form.task_type));
      fd.append("detailedDescription", form.detailedDescription);
      fd.append("priority",            form.priority);
      // ✅ Convert datetime-local format for backend
      fd.append("completion_sla",      form.completion_sla.replace("T", " "));
      fd.append("created_by",          form.created_by.value);
      fd.append("assigned_to",         form.assigned_to.value);
      fd.append("product_code",        String(task.product_code || ""));
      if (form.start_date)
        fd.append("start_date", form.start_date.replace("T", " "));
      if (form.subtaskComment.trim())
        fd.append("subtaskComment", form.subtaskComment);
      if (attachment)
        fd.append("attachment", attachment);
      form.observer.forEach(u => fd.append("observer", u.value));
      form.informer.forEach(u => fd.append("informer", u.value));
      form.functional_categories.forEach(c => fd.append("functional_categories", c));

      const res = await axiosInstance.post(
        `/task_assignment/create_subtask/${task._id}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.success) {
        showToast("Subtask created successfully!");
        onSuccess(); onClose();
      } else {
        showToast(res.data?.message || "Failed to create subtask", "error");
      }
    } catch { showToast("Error creating subtask", "error"); }
    finally   { setSubmitting(false); }
  };

  // DnD handlers
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) setAttachment(f);
  };

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @media (prefers-color-scheme: dark) {
          .rte-area { background: #fff !important; }
          .rte-body { color: #222 !important; caret-color: #222 !important; background: #fff !important; }
          .rte-toolbar { background: #f8f8f8 !important; border-color: #e0e0e0 !important; }
          .rte-btn { color: #444 !important; }
          .rte-sep { background: #d8d8d8 !important; }
          .rte-select { background-color: #fff !important; color: #333 !important; }
          .rte-footer { background: #f8f8f8 !important; color: #888 !important; }
          .rte-linkbar input { background: #fff !important; color: #333 !important; }
        }
      `}</style>

      {/* Backdrop */}
      <div onClick={onClose}
        className="fixed inset-0 z-[1600] bg-[rgba(15,23,42,.35)] backdrop-blur-sm" />

      {/* Offcanvas */}
      <div onClick={e => e.stopPropagation()}
        className="fixed top-0 right-0 bottom-0 w-[min(960px,98vw)] bg-slate-50 z-[1601] flex flex-col animate-[slideInRight_.3s_cubic-bezier(.4,0,.2,1)]">

        {/* ── Header ── */}
        <div className="bg-gradient-to-br from-[rgb(124,132,207)] to-[rgb(187,196,255)] px-5 py-3 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Plus size={15} color="#fff" />
              <span className="text-[14px] font-semibold text-white">Create Subtask</span>
            </div>
            <button onClick={onClose}
              className="w-[26px] h-[26px] rounded-[7px] bg-white/15 border border-white/30 flex items-center justify-center cursor-pointer">
              <X size={13} color="#fff" />
            </button>
          </div>
        </div>

        {/* ── Parent info bar ── */}
        <div className="px-5 py-2 bg-white border-b-[0.5px] border-b-[#E8EAF0] shrink-0 flex items-center">
          {[
            { label: "Workspace",       value: workspaceName || "—" },
            { label: "Parent Task",     value: task.task_type },
            { label: "Parent Deadline", value: parentDeadlineLabel || "—", color: "#185FA5" },
          ].map((item, i) => (
            <div key={i} className="flex items-center">
              {i > 0 && <span className="w-[0.5px] h-7 bg-[#E8EAF0] mx-4" />}
              <div>
                <div className="text-[9px] text-slate-400 font-semibold uppercase tracking-[.06em]">{item.label}</div>
                {/* color is dynamic (#185FA5 vs #374151), kept as inline style */}
                <div className="text-[12px] font-medium mt-px" style={{ color: item.color || "#374151" }}>
                  {item.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">

          {/* Subtask Name */}
          <div>
            <Label txt="Subtask Name" required />
            <input value={form.task_type}
              onChange={e => set("task_type", e.target.value)}
              placeholder="Enter subtask name"
              className={`${inputCls} ${errors.task_type ? 'border-red-300' : 'border-slate-200'}`} />
            {errors.task_type && <div className="text-[11px] text-red-500 mt-1">{errors.task_type}</div>}
          </div>

          {/* ✅ Detailed Description — RichTextEditor */}
          <div>
            <Label txt="Detailed Description" />
            <EditorWrapper
              editorNode={
                <RichTextEditor
                  theme="light"
                  key={`subtask-desc-${descKey}`}
                  value={form.detailedDescription}
                  onChange={html => set("detailedDescription", resizeImagesTo25(html))}
                />
              }
            />
          </div>

          {/* Priority + Functional Categories */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label txt="Priority" required />
              <select value={form.priority} onChange={e => set("priority", e.target.value)}
                className={`${inputCls} cursor-pointer`}>
                <option value="">Select Priority</option>
                {priorityList.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            {/* ✅ Functional Categories dropdown */}
            <CatSelect
              categories={categories}
              value={form.functional_categories}
              onChange={v => set("functional_categories", v)}
            />
          </div>

          {/* ✅ Start Date + Due Date — max = parent deadline */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label txt="Start Date" />
              <DateTimePicker
                value={form.start_date}
                min={parentCreatedAtMin}
                max={parentDeadlineMax}
                onChange={v => set("start_date", v)} />
              {parentDeadlineLabel && (
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Max: {parentDeadlineLabel} (parent deadline)
                </div>
              )}
            </div>
            <div>
              <Label txt="Due Date" required />
              <DateTimePicker
                value={form.completion_sla}
                min={form.start_date || parentCreatedAtMin}
                max={parentDeadlineMax}
                required
                onChange={v => set("completion_sla", v)} />
              {parentDeadlineLabel && (
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Max: {parentDeadlineLabel} (parent deadline)
                </div>
              )}
              {errors.completion_sla && <div className="text-[11px] text-red-500 mt-1">{errors.completion_sla}</div>}
            </div>
          </div>

          {/* Accountable + Responsible  */}
          <div className="grid grid-cols-2 gap-3">
            <UserSelect label="Accountable" value={form.created_by}
              onChange={v => set("created_by", v)} required error={errors.created_by} />
            <UserSelect label="Responsible" value={form.assigned_to}
              onChange={v => set("assigned_to", v)} required error={errors.assigned_to} />
          </div>

          {/* Consulted + Informed */}
          <div className="grid grid-cols-2 gap-3">
            <UserSelect label="Consulted" value={form.observer}
              onChange={v => set("observer", v)} multiple />
            <UserSelect label="Informed" value={form.informer}
              onChange={v => set("informer", v)} multiple />
          </div>

          {/* ✅ File Upload — above comment */}
          <div>
            <Label txt="Attachment" />
            <input ref={fileRef} type="file" className="hidden"
              accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
              onChange={e => setAttachment(e.target.files[0] || null)} />
            {attachment ? (
              <div className="flex items-center gap-2.5 px-3 py-[10px] bg-slate-50 border-[0.5px] border-[#E8EAF0] rounded-lg">
                <FileText size={16} color="#EF4444" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
                    {attachment.name}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {attachment.size > 1024 * 1024
                      ? `${(attachment.size / 1024 / 1024).toFixed(1)} MB`
                      : `${(attachment.size / 1024).toFixed(0)} KB`}
                  </div>
                </div>
                <button onClick={() => setAttachment(null)}
                  className="bg-red-100 border-none rounded-[6px] w-[22px] h-[22px] flex items-center justify-center cursor-pointer shrink-0">
                  <X size={10} color="#EF4444" />
                </button>
              </div>
            ) : (
              <div
                onDrop={onDrop}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onClick={() => fileRef.current.click()}
                className={`border-2 border-dashed rounded-lg p-3.5 text-center cursor-pointer transition-all ${dragging ? 'border-[rgb(124,132,207)] bg-[#EEEDFE20]' : 'border-gray-300 bg-slate-50'}`}>
                <Upload size={18} color={dragging ? "rgb(124,132,207)" : "#94A3B8"}
                  className="mx-auto mb-1 block" />
                <div className={`text-[12px] ${dragging ? 'text-[rgb(124,132,207)]' : 'text-slate-500'}`}>
                  {dragging ? "Drop file here!" : "Click to upload or drag & drop"}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  .jpg, .jpeg, .png, .pdf, .csv, .xlsx, .docx, .doc, .xls, .ppt, .pptx, .txt, .webp · Max 5MB
                </div>
              </div>
            )}
          </div>

          {/* Comment */}
          <div>
            <Label txt="Add Comment" />
            <textarea value={form.subtaskComment}
              onChange={e => set("subtaskComment", e.target.value)}
              placeholder="Add a comment (optional)"
              rows={2}
              className={`${inputCls} resize-y leading-relaxed`} />
          </div>

        </div>

        {/* ✅ Footer — right aligned, compact buttons */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t-[0.5px] border-t-[#E8EAF0] bg-white shrink-0">
          <button onClick={onClose}
            className="px-[18px] py-2 rounded-lg border-[0.5px] border-slate-200 bg-white text-[12px] font-semibold cursor-pointer text-slate-500 font-[inherit]">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting}
            className={`px-[18px] py-2 rounded-lg border-none bg-[rgb(124,132,207)] text-[12px] font-semibold text-white font-[inherit] flex items-center gap-[5px] ${submitting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
            {submitting
              ? <><Loader size={12} /> Creating...</>
              : <><Plus size={12} /> Create Subtask</>}
          </button>
        </div>

      </div>
    </>
  );
}